import React, { useMemo } from "react";
import Header from "./components/header";
import CardItem from "./components/card-item";
import Loading from "@/components/custom/loading";
import { useTranslation } from "react-i18next";
import { useGetAccountsByVesselId } from "@/services/queries/accounts";
import { Account } from "@/lib/types";
import { ACCOUNT_TYPES } from "@/lib/constants";
import { useGetCurrencies } from "@/services/queries/currency";
import useVessel from "@/store/use-vessel";

interface AccountWithIcon extends Account {
  icon: React.ReactNode;
}

const Balance = () => {
  const { t } = useTranslation();
  const { data: currenciesData, isPending: isCurrenciesPending } =
    useGetCurrencies();
  const { selectedVessel } = useVessel();
  const { data, isPending } = useGetAccountsByVesselId(selectedVessel?.id);

  const accounts = useMemo(() => {
    return data?.value || [];
  }, [data]);

  const currencies = useMemo(() => {
    return currenciesData?.value || [];
  }, [currenciesData]);

  const accountsWithIcons: AccountWithIcon[] = useMemo(
    () =>
      accounts
        ? accounts.map((account) => {
            const Icon = ACCOUNT_TYPES.find(
              (item) => item.value === account.type
            )?.icon;
            return {
              ...account,
              icon: Icon ? <Icon /> : null,
            };
          })
        : [],
    [accounts]
  );

  const accountsByGroup = useMemo(() => {
    return accountsWithIcons.reduce(
      (acc, account) => {
        (acc[account.type] = acc[account.type] || []).push(account);
        return acc;
      },
      {} as Record<string, AccountWithIcon[]>
    );
  }, [accountsWithIcons]);

  return (
    <>
      <title>{t("title.balance")}</title>
      <div>
        <Header />
        {isPending || isCurrenciesPending ? (
          <Loading className="mt-10" />
        ) : (
          <div className="space-y-[35px] px-5 pt-9">
            {accountsByGroup &&
              Object.entries(accountsByGroup).map(([category, accounts]) => (
                <div key={category} className="flex flex-col gap-[10px]">
                  <span className="text-base font-medium leading-5 text-white">
                    {t(
                      `account_type.${
                        ACCOUNT_TYPES.find((item) => item.value === category)
                          ?.translationKey
                      }`
                    )}
                  </span>
                  <div className="flex flex-col gap-[15px]">
                    {accounts.map((account) => (
                      <CardItem
                        currencies={currencies}
                        key={account.id}
                        item={account}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Balance;
